import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { teamAPI } from '../../services/api';
import { useUserStore } from '../../stores/userStore';
import { SettingsPanel } from './SettingsPanel';

interface Member {
  id: string;
  name: string;
  email: string;
  role: string;
}

const ROLE_LABEL: Record<string, string> = { OWNER: 'Owner', ADMIN: 'Admin', MEMBER: 'Member', VIEWER: 'Viewer' };

export function TeamSettings() {
  const team = useUserStore((s) => s.activeTeam);
  const org = useUserStore((s) => s.activeOrganization);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [failed, setFailed] = useState(false);

  const teamId = team?.id;

  useEffect(() => {
    if (!teamId) return;
    setMembers(null);
    setFailed(false);
    teamAPI.getMembers(teamId)
      .then((r) => setMembers(r.members as Member[]))
      .catch(() => setFailed(true));
  }, [teamId]);

  return (
    <SettingsPanel title={team ? team.name : 'Team'}>
      {org && <p className="-mt-2 text-sm text-muted-foreground">{org.name}</p>}
      {!team && <p className="text-sm text-muted-foreground">You are not in a team yet.</p>}
      {team && !members && !failed && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
      {failed && <p className="text-sm text-muted-foreground">Could not load the members of this team.</p>}
      {members && members.length > 0 && (
        <ul className="divide-y rounded-md border">
          {members.map((m) => (
            <li key={m.id} data-testid="team-member" className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate font-medium">{m.name}</div>
                <div className="truncate text-xs text-muted-foreground">{m.email}</div>
              </div>
              <span className="shrink-0 text-muted-foreground">{ROLE_LABEL[m.role] ?? m.role}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm text-muted-foreground">Ask your admin to add people.</p>
    </SettingsPanel>
  );
}
